// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type {
  GetLandingPageResponse,
  GetLandingPageV3Response,
  NonPurchasedProduct,
  ProductTrial,
  ProductV3,
  PurchasedProduct,
  StartSsoLink,
  TrialProductKey,
} from '../../../api/types';
import { productTags } from '../../constants';

export const TRIAL_PRODUCT_KEY: TrialProductKey = 'ipa';
export const TRIAL_WARNING_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

export type TrialState =
  | { kind: 'none' }
  | { kind: 'offer' }
  | { kind: 'active'; daysLeft: number }
  | { kind: 'ended' };

export type ActiveTrial = Extract<TrialState, { kind: 'active' }>;

export type LandingCard =
  | { kind: 'product'; product: PurchasedProduct; trial: ActiveTrial | null }
  | { kind: 'trial-ended'; displayName: string; productTag: string };

export type LandingView = {
  grid: LandingCard[];
  marketing: { product: NonPurchasedProduct; canStartTrial: boolean }[];
  trialEnded: boolean;
};

export const getCardName = (card: LandingCard): string =>
  card.kind === 'product' ? card.product.displayName : card.displayName;

export const getCardTag = (card: LandingCard): string =>
  card.kind === 'product' ? card.product.productTag : card.productTag;

// A tenant can have several products with the same tag, one per EAM enterprise.
export const getCardKey = (card: LandingCard): string =>
  card.kind === 'product' ? `${card.product.productTag}:${card.product.spMetadataId}` : card.productTag;

const PRODUCT_TAG_BY_KEY: Partial<Record<string, productTags>> = {
  eam: productTags.EAM,
  mam: productTags.MAM,
  aip: productTags.AIP,
  ipa: productTags.PATIENT_ACCESS,
  vpam: productTags.VPAM,
  itdr: productTags.ITDR,
  eamc: productTags.SSO,
};

const isTrialRunning = (trial: ProductV3['trial'], now: number): boolean =>
  !!trial.startedAt && !!trial.expiresAt && Date.parse(trial.expiresAt) > now;

const toLaunchLinks = (urls: { name?: string; url: string }[]): StartSsoLink[] =>
  urls.map(({ name, url }) => ({ startSsoUrl: url, applianceDesignator: name ?? '' }));

const toPurchasedProduct = (
  product: ProductV3,
  startSsoLinks: StartSsoLink[],
  trial?: ProductTrial,
): PurchasedProduct => ({
  displayName: product.name,
  integrationId: '',
  productTag: PRODUCT_TAG_BY_KEY[product.key] ?? product.key,
  spDisplayName: product.spMetadata?.displayName ?? '',
  spMetadataId: product.spMetadata?.id ?? '',
  startSsoLinks,
  trial,
});

const toTrial = (product: ProductV3, now: number): ProductTrial => ({
  productKey: product.key,
  eligible: product.trial.offered && product.trial.eligible,
  active: isTrialRunning(product.trial, now),
  startedAt: product.trial.startedAt ?? null,
  expiresAt: product.trial.expiresAt ?? null,
});

// The landing page's own launch url wins; the one from the start response only covers the gap
// until the landing page reports it.
const toRunningTrialProduct = (
  product: ProductV3,
  trial: ProductTrial,
  startedLaunchUrl: StartedLaunchUrl,
): PurchasedProduct => {
  const launchUrl = product.trial.launchUrl ?? startedLaunchUrl(product.key);
  if (!launchUrl) {
    console.error('Running trial has no launch url', product.key);
  }
  return toPurchasedProduct(product, launchUrl ? toLaunchLinks([{ url: launchUrl }]) : [], trial);
};

const toNonPurchasedProduct = (product: ProductV3, trial: ProductTrial): NonPurchasedProduct => ({
  displayName: product.name,
  productTag: PRODUCT_TAG_BY_KEY[product.key] ?? product.key,
  learnMoreUrl: product.documentationUrl ?? '',
  trial,
});

export type StartedLaunchUrl = (productKey: string) => string | undefined;

export const fromLandingPageV3 = (
  data: GetLandingPageV3Response,
  now: number,
  startedLaunchUrl: StartedLaunchUrl = () => undefined,
): GetLandingPageResponse => {
  const purchasedProducts: PurchasedProduct[] = data.productInfo.purchasedProducts
    .filter(product => product.available)
    .map(product => toPurchasedProduct(product, toLaunchLinks(product.launchUrls)));
  const nonPurchasedProducts: NonPurchasedProduct[] = [];

  for (const product of data.productInfo.nonPurchasedProducts.filter(item => item.available)) {
    const trial = toTrial(product, now);
    if (trial.active) {
      purchasedProducts.push(toRunningTrialProduct(product, trial, startedLaunchUrl));
    } else {
      nonPurchasedProducts.push(toNonPurchasedProduct(product, trial));
    }
  }

  return { orgInfo: data.orgInfo, productInfo: { purchasedProducts, nonPurchasedProducts } };
};

export const getTrialDaysLeft = (expiresAt: string, now: number): number | null => {
  const expiresAtMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return null;
  }
  return Math.max(1, Math.ceil((expiresAtMs - now) / DAY_MS));
};

export const getTrialState = (trial: ProductTrial | undefined, now: number): TrialState => {
  if (!trial) {
    return { kind: 'none' };
  }

  if (trial.active) {
    const daysLeft = trial.expiresAt ? getTrialDaysLeft(trial.expiresAt, now) : null;
    if (daysLeft === null) {
      console.error('Active trial has an unusable expiresAt', trial.productKey, trial.expiresAt);
      return { kind: 'none' };
    }
    return { kind: 'active', daysLeft };
  }

  if (!trial.eligible && trial.expiresAt !== null) {
    return { kind: 'ended' };
  }

  if (trial.eligible) {
    return { kind: 'offer' };
  }

  return { kind: 'none' };
};

const trialStateFor = (
  productTag: string,
  trial: ProductTrial | undefined,
  now: number,
): TrialState => (productTag === productTags.PATIENT_ACCESS ? getTrialState(trial, now) : { kind: 'none' });

const toTrialEndedCard = ({ displayName, productTag }: PurchasedProduct | NonPurchasedProduct): LandingCard => ({
  kind: 'trial-ended',
  displayName,
  productTag,
});

export const toLandingView = (
  data: GetLandingPageResponse | undefined,
  ctx: { isAdmin: boolean; now: number },
): LandingView => {
  const purchasedProducts = data?.productInfo?.purchasedProducts ?? [];
  const nonPurchasedProducts = data?.productInfo?.nonPurchasedProducts ?? [];
  const purchasedTags = new Set(purchasedProducts.map(product => product.productTag));

  const grid: LandingCard[] = [];
  const marketing: LandingView['marketing'] = [];

  for (const product of purchasedProducts) {
    const trialState = trialStateFor(product.productTag, product.trial, ctx.now);

    if (trialState.kind === 'ended') {
      grid.push(toTrialEndedCard(product));
      continue;
    }

    grid.push({
      kind: 'product',
      product,
      trial: trialState.kind === 'active' ? trialState : null,
    });
  }

  for (const product of nonPurchasedProducts) {
    if (purchasedTags.has(product.productTag)) {
      continue;
    }

    const trialState = trialStateFor(product.productTag, product.trial, ctx.now);

    if (trialState.kind === 'ended') {
      grid.push(toTrialEndedCard(product));
      continue;
    }

    marketing.push({ product, canStartTrial: trialState.kind === 'offer' && ctx.isAdmin });
  }

  return { grid, marketing, trialEnded: grid.some(card => card.kind === 'trial-ended') };
};
